// Phase 1A Step 37 — Unified Shell foundation.
//
// Additive shell component for the unified institutional product
// experience defined in docs/mode-architecture-plan.md and the
// handoff_phantom_mode prototype. Step 37 is FOUNDATION ONLY — this
// component exists in the codebase but is not consumed by any live
// route. Step 38 will introduce the first consumer (`/home`).
//
// Anatomy (per handoff_phantom_mode/SCREENS.md + prototype/shell.jsx):
//   - 36px context strip (sticky top): wordmark + optional eyebrow +
//     optional title + optional chain summary + optional safety
//     indicator + account-dot placeholder. Dims to 50% opacity when
//     `dimContext` is true (council mode reserves the strip but
//     visually de-emphasises it).
//   - 56px right rail: 5 mode buttons (Pitch / Vault / Brief / Council
//     / Exchange). Active mode highlighted with amber left border and
//     amber icon color. Tooltip on hover. Optional `onNavigate`
//     callback — if absent, buttons render as inert visual indicators
//     (Step 37 is foundation-only; route wiring lands later).
//   - Main content area: receives `children`, no chrome of its own.
//
// HARD CONSTRAINTS:
//   - No data fetching, no Supabase, no localStorage, no fetch().
//   - No icon library. Adinkra-derived placeholders are SVG only.
//   - No emoji.
//   - No react-router-dom imports — rail uses plain <button>s + an
//     optional callback prop.
//   - All colors via var(--sz-*) tokens.
//   - No file > 250 LOC.

import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Eyebrow } from "../components/atoms/index.ts";
import type { ModeKey } from "../modes/mode-config.ts";
import { UNIFIED_MODE_ICONS, StrikzaMark } from "./unified-shell-icons.tsx";
import * as s from "./unified-shell.styles.ts";

export type UnifiedMode = ModeKey;

export interface UnifiedShellProps {
  /** Currently active mode — drives the rail's active-state styling. */
  mode: UnifiedMode;
  /** Page content rendered inside the main column. */
  children: ReactNode;
  /** Optional eyebrow rendered in the context strip (e.g. cycle phase). */
  contextEyebrow?: string;
  /** Optional secondary label rendered next to the eyebrow. */
  contextTitle?: string;
  /** Pre-formatted chain summary (e.g. "GH₵2,700"). Caller formats. */
  chainSummary?: string;
  /** Pre-formatted safety label (e.g. "SAFE MODE"). Defaults shown if absent. */
  safetyLabel?: string;
  /** Letters rendered inside the account-dot placeholder (e.g. "P", "SP"). */
  accountLabel?: string;
  /** When true, dims the context strip to 50% (used inside The Council). */
  dimContext?: boolean;
  /** Optional navigation callback. If omitted, rail buttons stay inert. */
  onNavigate?: (next: UnifiedMode) => void;
}

const RAIL_ORDER: readonly UnifiedMode[] = ["pitch", "vault", "brief", "council", "exchange"];

export function UnifiedShell({
  mode,
  children,
  contextEyebrow,
  contextTitle,
  chainSummary,
  safetyLabel = "SAFE MODE",
  accountLabel = "",
  dimContext = false,
  onNavigate,
}: UnifiedShellProps): JSX.Element {
  return (
    <div style={s.shell}>
      <ContextStrip
        contextEyebrow={contextEyebrow}
        contextTitle={contextTitle}
        chainSummary={chainSummary}
        safetyLabel={safetyLabel}
        accountLabel={accountLabel}
        dimContext={dimContext}
      />
      <div style={s.body}>
        <main style={s.main}>{children}</main>
        <UnifiedRail mode={mode} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

// ─── Context strip (internal) ─────────────────────────────────────

interface ContextStripProps {
  contextEyebrow?: string;
  contextTitle?: string;
  chainSummary?: string;
  safetyLabel: string;
  accountLabel: string;
  dimContext: boolean;
}

function ContextStrip({
  contextEyebrow,
  contextTitle,
  chainSummary,
  safetyLabel,
  accountLabel,
  dimContext,
}: ContextStripProps): JSX.Element {
  const stripStyle: CSSProperties = dimContext ? s.stripDimmed : s.strip;
  return (
    <header style={stripStyle} role="banner">
      <span style={s.stripWordmark}>
        <StrikzaMark size={13} />
        STRIKZA
      </span>
      {(contextEyebrow ?? contextTitle) ? <span style={s.stripSep} aria-hidden="true" /> : null}
      {contextEyebrow ? (
        <Eyebrow muted noLine style={{ marginRight: contextTitle ? "var(--sz-space-3)" : 0 }}>
          {contextEyebrow}
        </Eyebrow>
      ) : null}
      {contextTitle ? <span style={s.stripContextLabel}>{contextTitle}</span> : null}
      <span style={s.stripSpacer} />
      {chainSummary ? (
        <>
          <span style={s.stripChainLabel}>CHAIN</span>
          <span style={s.stripChainValue}>{chainSummary}</span>
          <span style={s.stripSep} aria-hidden="true" />
        </>
      ) : null}
      <span style={s.stripSafety} title="Safe Mode is active">
        <span style={s.stripSafetyDot} aria-hidden="true" />
        {safetyLabel}
      </span>
      <span style={s.stripSep} aria-hidden="true" />
      <span
        style={s.stripAccountDot}
        role="img"
        aria-label="Account menu placeholder"
        title="Account placeholder · menu not wired in Phase 1A"
      >
        {accountLabel}
      </span>
    </header>
  );
}

// ─── Right rail (internal) ────────────────────────────────────────

interface UnifiedRailProps {
  mode: UnifiedMode;
  onNavigate?: (next: UnifiedMode) => void;
}

function UnifiedRail({ mode, onNavigate }: UnifiedRailProps): JSX.Element {
  const [hovered, setHovered] = useState<UnifiedMode | null>(null);

  return (
    <aside style={s.rail} aria-label="Mode switcher">
      {RAIL_ORDER.map((key) => {
        const entry = UNIFIED_MODE_ICONS[key];
        const isActive = mode === key;
        const itemStyle: CSSProperties = isActive ? s.railItemActive : s.railItem;
        const iconColor = isActive ? "var(--sz-amber-text)" : "var(--sz-text-muted)";
        return (
          <button
            type="button"
            key={key}
            style={itemStyle}
            onMouseEnter={() => setHovered(key)}
            onMouseLeave={() => setHovered(null)}
            onClick={onNavigate ? () => onNavigate(key) : undefined}
            disabled={!onNavigate}
            aria-label={entry.label}
            aria-current={isActive ? "page" : undefined}
            title={entry.label}
          >
            <entry.Icon size={22} color={iconColor} />
            {hovered === key ? (
              <div style={s.railTooltip} role="tooltip">
                {entry.label}
                <span style={s.railTooltipSymbol}>· {entry.symbol}</span>
              </div>
            ) : null}
          </button>
        );
      })}
      <div style={s.railVersionWrap}>
        <div style={s.railVersionText}>v 0.1 · 1A</div>
      </div>
    </aside>
  );
}
